/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @private
 * @typedef {Object} DefaultFromSlot
 * @property {String} id  - Slot ID to use for defaults
 */

/**
 * @interface SlotDefinitionAPI
 * @classdesc API class that is used to control slot definitions.
 * @example slot.getDefinition()
 */
define([], () => {
	class SlotDefinitionAPI {

		/**
		 * @function SlotDefinitionAPI#getProperty
		 * @description Gets a property value from the slot definition.
		 * @private
		 * @param {String} id
		 * @returns {Object} Property value
		 */
		getProperty() {}

		/**
		 * @function SlotDefinitionAPI#getDatasetIdList
		 * @description Gets the data set ID list.
		 * @private
		 *
		 * @return {Array} Data set ID string array representation
		 */
		getDatasetIdList() {}

		/**
		 * @function SlotDefinitionAPI#getId
		 * @description Gets the slot ID.
		 * @public
		 *
		 * @return {String} Slot ID
		 */
		getId() {}

		/**
		 * @function SlotDefinitionAPI#getCaption
		 * @description Gets the slot caption.
		 * @public
		 *
		 * @return {String} Slot caption
		 */
		getCaption() {}

		/**
		 * @function SlotDefinitionAPI#getIcon
		 * @description Gets the slot icon name.
		 * @private
		 *
		 * @return {String} Slot CSS icon name
		 */
		getIcon() {}

		/**
		 * @function SlotDefinitionAPI#getType
		 * @description Gets the slot type.
		 * @public
		 *
		 * @return {String} Slot type
		 */
		getType() {}

		/**
		 * @function SlotDefinitionAPI#getSubType
		 * @description Gets the slot sub type, such as location, latitude, longitude, etc.
		 * @private
		 *
		 * @return {String} Slot sub type
		 */
		getSubType() {}

		/**
		 * @function SlotDefinitionAPI#getGroupId
		 * @description Gets the slot group ID.
		 * @private
		 *
		 * @return {String} Slot group ID
		 */
		getGroupId() {}

		/**
		 * @function SlotDefinitionAPI#getRole
		 * @description Gets the slot role.
		 * @private
		 *
		 * @return {String} Slot role
		 */
		getRole() {}

		/**
		 * @function SlotDefinitionAPI#isHiden
		 * @description Checks whether the slot is hidden or not.
		 * @private
		 *
		 * @return {boolean} Returns true if slot is hidden, otherwise false.
		 */
		isHidden() {}

		/**
		 * @function SlotDefinitionAPI#isOptional
		 * @description Checks whether the slot is optional or not.
		 * @private
		 *
		 * @return {boolean} Returns true if slot is optional, otherwise false.
		 */
		isOptional() {}

		/**
		 * @function SlotDefinitionAPI#getSort
		 * @description Gets the slot sort.
		 * @private
		 *
		 * @return {String} Slot sort
		 */
		getSort() {}

		/**
		 * @function SlotDefinitionAPI#getSortOrder
		 * @description Gets the slot sort order.
		 * @private
		 *
		 * @return {Number} Slot sort order represented by number
		 */
		getSortOrder() {}

		/**
		 * @function SlotDefinitionAPI#getAggregatedSortInfo
		 * @description Gets aggregated and sort info used when sort on aggregated fact values.
		 * @private
		 *
		 * @return {Object} clientSideAggregatedAndSortInfo object in slot definition
		 * @example
		 * {
		 *    clientSideAggregatedAndSortInfo:{
		 *        mappedSlotCondition:[
		 *            'series'
		 *        ],
		 *        groupOnColumnId :'categories'
		 *    }
		 * }
		 */
		getAggregatedSortInfo() {}

		/**
		 * @function SlotDefinitionAPI#isSortable
		 * @description Checks whether the slot is sortable or not.
		 * @private
		 *
		 * @return {boolean} Returns true if slot is sortable, otherwise false.
		 */
		isSortable() {}

		/**
		 * @function SlotDefinitionAPI#isStackItems
		 * @description Checks whether the slot can be have stacked items.
		 * @private
		 *
		 * @return {boolean} Returns true if slot can have stacked items, otherwise false.
		 */
		isStackItems() {}

		/**
		 * @function SlotDefinitionAPI#groupLeadingItems
		 * @description Checks whether the slot's data items should follow the "group leading items"
		 * pattern, which is typical on a Dashboard table, i.e. all data items of type 'attribute'
		 * that occur before a data item of type 'fact' are grouped; 'fact' data items are not grouped
		 * and data items that occur following a 'fact' data item are not grouped.
		 * @example With data items: 1. Product line, 2. Product type, 3. Year, 4. Revenue,
		 * 	5. Order method type, 6. Quantity; data items 1, 2 and 3 are grouped (they are leading
		 * 	attributes), 4, 5 and 6 are not grouped (4 is a fact, so it and all remaing data items
		 * 	are not grouped)
		 * @private
		 *
		 * @return {boolean} Returns true if slot has grouped leading items, otherwise false.
		 */
		groupLeadingItems() {}

		/**
		 * @function SlotDefinitionAPI#getMaxItems
		 * @description Gets the maximum number of items allowed on a slot.
		 * @private
		 *
		 * @return {Number} Maximum number of items allowed on slot
		 */
		getMaxItems() {}

		/**
		 * @function SlotDefinitionAPI#isMultiMeasureSupported
		 * @description Checks whether the slot can have multiple measures.
		 * @private
		 *
		 * @return {boolean} Returns true if the slot supports multiple measures, otherwise false.
		 */
		isMultiMeasureSupported() {}

		/**
		 * @function SlotDefinitionAPI#isShapable
		 * @description Check whether the slot can support graphical shapes.
		 * @private
		 *
		 * @return {boolean} Returns true if the slot supports shapes, otherwise false.
		 */
		isShapable() {}

		/**
		 * @function SlotDefinitionAPI#getCoachMark
		 * @description Gets the coach mark object for the slot.
		 * @private
		 *
		 * @return {Object} Coach mark object
		 * @example
		 * {
		 * 		titleResource: 'coachMarkTitleSpiralTarget',
		 * 		contentResource: 'coachMarkContentSpiralTarget',
		 * 		mappedOnly: true,	// show only when the slot is mapped
		 * 		showPopover: true	// show popover with the coach mark
		 * }
		 */
		getCoachMark() {}

		/**
		 * @function SlotDefinitionAPI#getFormat
		 * @description Gets the slot format.
		 * @private
		 *
		 * @return {Object} Slot formatting
		 */
		getFormat() {}

		/**
		 * @function SlotDefinitionAPI#getTags
		 * @description Gets the slot tags.
		 * Tags identify special uses for slots in a visualization and include:
		 * 'badge' - this slot can be marked with a badge (and badge information must be requested for its items)
		 * 'ca.dashboard.repeatHere' - This slot is the default where multiMeasure series are assigned (eg: color for a barchart).
		 * @private
		 *
		 * @return {String[]} Slot tags
		 */
		getTags() {}

		/**
		 * @function SlotDefinitionAPI#getDefaultFromSlot
		 * @description Gets the slot information that should be used to default this slot.
		 * @private
		 * @return {DefaultFromSlot}
		 */
		getDefaultFromSlot() {}

		/**
		 * @function SlotDefinitionAPI#getShowRequiredMarker
		 * @description Should the required marker be shown in the UI
		 * @private
		 * @returns {Boolean}
		 */
		getShowRequiredMarker() {}
	}


	return SlotDefinitionAPI;
});
